// src/pages/admin/fees/FeePlans.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listPlans, activatePlan, archivePlan } from "../../../api/fees";
import { fetchBranches } from "../../../api/branches";
import { useNavigate } from "react-router-dom";
import PaginationFooter from "../../../components/common/PaginationFooter";
import SortableTableHeader from "../../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../../hooks/useSortedClientPagination";
import { feePlanSortColumns } from "../../../config/tableSortColumns";
import ListPageLayout, {
    ListPageHeader,
    ListLoadingSpinner,
    ListTableCard,
} from "../../../components/layout/ListPageLayout";
import { showConfirm } from '../../../utils/popup';
import { formatCurrency } from '../../../utils/currency';
import { branchDisplayName } from '../../../utils/displayNames';

const PLAN_TYPES = ["PACKAGE", "MONTHLY", "PER_SESSION"];
const GROUP_TYPES = ["GROUP", "ONE_TO_ONE"];
const DELIVERY = ["ANY", "ONLINE", "OFFLINE"];

export default function FeePlans() {
    const { t } = useTranslation();
    const [branches, setBranches] = useState([]);
    const [branchesLoadError, setBranchesLoadError] = useState(false);
    const [branchId, setBranchId] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [busyId, setBusyId] = useState(null); // for activate/archive in-flight

    const navigate = useNavigate();

    const branchName = useMemo(() => {
        const m = {};
        for (const b of branches) m[b.id] = b.name || branchDisplayName(null, b.id, t);
        return m;
    }, [branches, t]);

    // Load active branches for dropdown
    useEffect(() => {
        (async () => {
            try {
                const res = await fetchBranches();
                const list = res?.data ?? [];
                const active = Array.isArray(list) ? list.filter(b => b.active !== false) : [];
                setBranches(active);
                setBranchesLoadError(false);
            } catch (e) {
                console.error(e);
                setBranches([]);
                setBranchesLoadError(true);
            }
        })();
    }, []);

    // Fetch plans for selected branch (include inactive so we can Activate/Archive)
    useEffect(() => {
        let alive = true;
        (async () => {
            setRows([]);
            setLoading(true);
            setError("");
            try {
                if (!branchId) return;
                const data = await listPlans({ branchId: Number(branchId), includeInactive: true });
                const arr = data?.content ?? data ?? [];
                if (!alive) return;
                setRows(Array.isArray(arr) ? arr : []);
            } catch (e) {
                console.error(e);
                setError(t("pages.feePlans.loadFailed"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [branchId]);

    const refresh = async () => {
        if (!branchId) return;
        setLoading(true);
        setError("");
        try {
            const data = await listPlans({ branchId: Number(branchId), includeInactive: true });
            const arr = data?.content ?? data ?? [];
            setRows(Array.isArray(arr) ? arr : []);
        } catch (e) {
            console.error(e);
            setError(t("pages.feePlans.loadFailed"));
        } finally {
            setLoading(false);
        }
    };

    const onArchive = async (planId) => {
        if (!planId) return;
        if (!await showConfirm(t("pages.feePlans.archiveConfirm"))) return;
        try {
            setBusyId(planId);
            await archivePlan(planId);
            await refresh();
        } catch (e) {
            console.error(e);
            alert(t("pages.feePlans.archiveFailed"));
        } finally {
            setBusyId(null);
        }
    };

    const onActivate = async (planId) => {
        if (!planId) return;
        try {
            setBusyId(planId);
            await activatePlan(planId);
            await refresh();
        } catch (e) {
            console.error(e);
            alert(t("pages.feePlans.activateFailed"));
        } finally {
            setBusyId(null);
        }
    };

    const shown = rows;

    const pagination = useSortedClientPagination(shown, {
        columns: feePlanSortColumns,
        resetKey: branchId,
    });

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t("pages.feePlans.title")}
                subtitle={t("pages.feePlans.subtitle")}
                actions={(
                    <>
                        <select className="input max-w-xs"
                            value={branchId}
                            onChange={(e) => setBranchId(e.target.value)}
                        >
                            <option value="">{t("pages.feePlans.selectBranch")}</option>
                            {branches.map(b => (
                                <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="btn-page-action-primary"
                            onClick={() => navigate(`/admin/fees/plans/new${branchId ? `?branchId=${branchId}` : ""}`)}
                        >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t("pages.feePlans.addPlan")}
                        </button>
                    </>
                )}
            />

            {branchesLoadError && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
                    {t('common.optionsLoadFailed')}
                </div>
            )}

            <ListTableCard>
                <table className="min-w-full">
                    <thead className="table-header">
                        <tr>
                            <SortableTableHeader label={t("pages.feePlans.colPlan")} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                            <SortableTableHeader label={t("pages.feePlans.colBranch")} columnKey="branch" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                            <SortableTableHeader label={t("common.code")} columnKey="code" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                            <SortableTableHeader label={t("pages.feePlans.colType")} columnKey="planType" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                            <SortableTableHeader label={t("pages.feePlans.colGroup")} columnKey="groupType" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                            <SortableTableHeader label={t("pages.feePlans.colRate")} columnKey="rate" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                            <SortableTableHeader label={t("pages.feePlans.colCredits")} columnKey="credits" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                            <th className="table-header-cell">{t("pages.feePlans.colLevelScope")}</th>
                            <th className="table-header-cell">{t("common.actions")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {!branchId ? (
                            <tr><td className="p-4 text-gray-500" colSpan={11}>{t("pages.feePlans.selectBranchHint")}</td></tr>
                        ) : loading ? (
                            <tr><td className="p-0" colSpan={11}><ListLoadingSpinner /></td></tr>
                        ) : error ? (
                            <tr><td className="p-4 text-red-600" colSpan={11}>{error}</td></tr>
                        ) : shown.length === 0 ? (
                            <tr><td className="p-4 text-gray-500" colSpan={11}>{t("pages.feePlans.noPlans")}</td></tr>
                        ) : pagination.pageItems.map((r) => (
                            <tr key={r.planId} className="table-row">
                                <td className="table-cell">#{r.planId}</td>
                                <td className="table-cell">{branchName[r.branchId] ? `${branchName[r.branchId]} (#${r.branchId})` : (r.branchId ? `#${r.branchId}` : "—")}</td>
                                <td className="table-cell">{r.code}</td>
                                <td className="table-cell">{t(`pages.feePlanForm.planTypes.${r.planType}`, { defaultValue: r.planType })}</td>
                                <td className="table-cell">{t(`pages.feePlanForm.groupTypes.${r.groupType}`, { defaultValue: r.groupType })}</td>
                                <td className="table-cell">{r.defaultRate != null ? formatCurrency(r.defaultRate) : "-"}</td>
                                <td className="table-cell">{r.defaultCredits ?? "-"}</td>
                                <td className="table-cell">
                                    {r.levelMin ? `L${r.levelMin}–L${r.levelMax}` : "-"}
                                </td>
                                <td className="table-cell whitespace-nowrap">
                                    {r.active ? (
                                        <button
                                            type="button"
                                            onClick={() => onArchive(r.planId)}
                                            disabled={busyId === r.planId}
                                            className="btn-action-archive disabled:cursor-not-allowed disabled:opacity-50"
                                            title={t("pages.feePlans.archiveTitle")}
                                        >
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                            </svg>
                                            {busyId === r.planId ? "..." : t("common.archive")}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => onActivate(r.planId)}
                                            disabled={busyId === r.planId}
                                            className="btn-action-activate disabled:cursor-not-allowed disabled:opacity-50"
                                            title={t("pages.feePlans.activateTitle")}
                                        >
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            {busyId === r.planId ? "..." : t("common.activate")}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </ListTableCard>

            <PaginationFooter
                page={pagination.page}
                size={pagination.size}
                total={pagination.total}
                onPageChange={pagination.setPage}
                onSizeChange={pagination.setSize}
            />
        </ListPageLayout>
    );
}
